/**
 * 商品管理页面 - 管理员发布和管理商品
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import { productAdminApi, productApi, type Product, type ProductCategory } from '@/services/productApi';
import { useAuthStore } from '@/stores/auth';
import { Loader2, Plus, Edit, Trash2, Eye, EyeOff, Upload } from 'lucide-react';
import { toast } from 'sonner';

export function ProductManagementPage() {
  const { accessToken, user } = useAuthStore();
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [coverImage, setCoverImage] = useState<string | null>(null);

  // 表单状态
  const [formData, setFormData] = useState({
    productId: '',
    name: '',
    subtitle: '',
    description: '',
    category: '',
    price: '',
    originalPrice: '',
    coverImageUrl: '',
    imageUrls: '',
    purchaseUrl: '',
    purchasePlatform: '',
    sortOrder: '0',
  });

  // 加载数据
  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      toast.error('需要管理员权限');
      navigate('/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    if (!accessToken) return;

    setLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        productAdminApi.getAllProducts(accessToken, { page: 0, size: 100 }),
        productApi.getCategories(),
      ]);
      setProducts(productsData.list);
      setCategories(categoriesData);
    } catch (error) {
      console.error('加载数据失败:', error);
      toast.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开新增对话框
  const handleAdd = () => {
    setEditingProduct(null);
    setUploadedImages([]);
    setCoverImage(null);
    setFormData({
      productId: '',
      name: '',
      subtitle: '',
      description: '',
      category: '',
      price: '',
      originalPrice: '',
      coverImageUrl: '',
      imageUrls: '',
      purchaseUrl: '',
      purchasePlatform: '',
      sortOrder: '0',
    });
    setShowDialog(true);
  };

  // 处理图片上传
  const handleImageUpload = async (files: FileList) => {
    if (!accessToken) {
      toast.error('未登录');
      return;
    }

    setUploadingImage(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const result = await productAdminApi.uploadImage(file, accessToken);
        setUploadedImages(prev => [...prev, result.url]);
        toast.success(`图片 ${i + 1} 上传成功`);
      }
    } catch (error: any) {
      console.error('图片上传失败:', error);
      toast.error(error.message || '图片上传失败');
    } finally {
      setUploadingImage(false);
    }
  };

  // 删除已上传的图片
  const handleRemoveImage = (url: string) => {
    setUploadedImages(prev => prev.filter(u => u !== url));
    if (coverImage === url) {
      setCoverImage(null);
    }
  };

  // 设置封面图
  const handleSetCover = (url: string) => {
    setCoverImage(url);
  };

  // 打开编辑对话框
  const handleEdit = (product: Product) => {
    setEditingProduct(product);

    // 合并所有图片：确保 imageUrls 和 coverImageUrl 都被包含
    let allImages: string[] = [];
    if (product.imageUrls && product.imageUrls.length > 0) {
      allImages = [...product.imageUrls];
    }
    // 如果封面图不在 imageUrls 中，添加进去
    if (product.coverImageUrl && !allImages.includes(product.coverImageUrl)) {
      allImages.unshift(product.coverImageUrl);
    }

    setUploadedImages(allImages);
    setCoverImage(product.coverImageUrl || null);
    setFormData({
      productId: product.productId,
      name: product.name,
      subtitle: product.subtitle || '',
      description: product.description || '',
      category: product.category,
      price: product.price.toString(),
      originalPrice: product.originalPrice?.toString() || '',
      coverImageUrl: product.coverImageUrl || '',
      imageUrls: product.imageUrls?.join('\n') || '',
      purchaseUrl: product.purchaseUrl || '',
      purchasePlatform: product.purchasePlatform || '',
      sortOrder: product.sortOrder.toString(),
    });
    setShowDialog(true);
  };

  // 保存商品
  const handleSave = async () => {
    if (!formData.productId || !formData.name || !formData.category || !formData.price) {
      toast.error('请填写必填项');
      return;
    }

    setSubmitting(true);
    try {
      // 确保封面图在 imageUrls 中
      const finalImageUrls = [...uploadedImages];
      if (coverImage && !finalImageUrls.includes(coverImage)) {
        finalImageUrls.unshift(coverImage);
      }

      const data = {
        productId: formData.productId,
        name: formData.name,
        subtitle: formData.subtitle || undefined,
        description: formData.description || undefined,
        category: formData.category,
        price: Number.parseFloat(formData.price),
        originalPrice: formData.originalPrice ? Number.parseFloat(formData.originalPrice) : undefined,
        coverImageUrl: coverImage || undefined,
        // 如果没有上传图片但有封面图，至少保存封面图
        imageUrls: finalImageUrls.length > 0 ? finalImageUrls : (coverImage ? [coverImage] : undefined),
        purchaseUrl: formData.purchaseUrl || undefined,
        purchasePlatform: formData.purchasePlatform || undefined,
        sortOrder: Number.parseInt(formData.sortOrder),
        status: 1,
      };

      console.log('保存商品数据:', data);

      if (editingProduct) {
        await productAdminApi.updateProduct(editingProduct.productId, data, accessToken!);
        toast.success('商品更新成功');
      } else {
        await productAdminApi.createProduct(data, accessToken!);
        toast.success('商品创建成功');
      }

      setShowDialog(false);
      loadData();
    } catch (error: any) {
      console.error('保存商品失败:', error);
      toast.error(error.message || '保存商品失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 上架/下架商品
  const handleToggleStatus = async (product: Product) => {
    try {
      if (product.status === 1) {
        await productAdminApi.unpublishProduct(product.productId, accessToken!);
        toast.success('商品已下架');
      } else {
        await productAdminApi.publishProduct(product.productId, accessToken!);
        toast.success('商品已上架');
      }
      loadData();
    } catch (error: any) {
      console.error('切换状态失败:', error);
      toast.error(error.message || '操作失败');
    }
  };

  // 删除商品
  const handleDelete = async (product: Product) => {
    if (!confirm(`确定要删除商品 "${product.name}" 吗？`)) {
      return;
    }

    try {
      await productAdminApi.deleteProduct(product.productId, accessToken!);
      toast.success('商品删除成功');
      loadData();
    } catch (error: any) {
      console.error('删除商品失败:', error);
      toast.error(error.message || '删除商品失败');
    }
  };

  // 查看商品详情
  const handleViewDetail = (product: Product) => {
    navigate(`/mall/product/${product.productId}`);
  };

  if (loading) {
    return (
      <LayoutWithSidebar title="商品管理">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-600">加载中...</span>
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar title="商品管理">
      <div className="min-h-screen bg-gray-50">
      {/* 页头 */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">商品管理</h1>
              <p className="text-gray-600 mt-1">发布和管理商城商品</p>
            </div>
            <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-5 w-5 mr-2" />
              新增商品
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* 商品列表 */}
        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">商品信息</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">分类</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">价格</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">排序</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.productId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {product.coverImageUrl && (
                          <img
                            src={product.coverImageUrl}
                            alt={product.name}
                            className="h-12 w-12 rounded object-cover mr-4"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          {product.subtitle && (
                            <div className="text-sm text-gray-500">{product.subtitle}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">{product.productId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.category}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <span className="font-medium text-red-600">¥{product.price.toFixed(2)}</span>
                        {product.originalPrice && (
                          <span className="text-gray-400 line-through ml-2">
                            ¥{product.originalPrice.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={product.status === 1 ? 'default' : 'secondary'}>
                        {product.status === 1 ? '已上架' : '已下架'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{product.sortOrder}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewDetail(product)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(product)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleStatus(product)}
                        >
                          {product.status === 1 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(product)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 新增/编辑对话框 */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? '编辑商品' : '新增商品'}
            </DialogTitle>
            <DialogDescription>
              {editingProduct ? '修改商品信息' : '创建新的商品'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="productId">商品ID *</Label>
                <Input
                  id="productId"
                  value={formData.productId}
                  onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                  disabled={!!editingProduct}
                  placeholder="PROD001"
                />
              </div>
              <div>
                <Label htmlFor="name">商品名称 *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="脉语灵环"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subtitle">副标题</Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder="负空间的精准表达"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">分类 *</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">请选择分类</option>
                  {categories.map((cat) => (
                    <option key={cat.code} value={cat.code}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="sortOrder">排序权重</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                  placeholder="100"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">价格 *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="999.00"
                />
              </div>
              <div>
                <Label htmlFor="originalPrice">原价</Label>
                <Input
                  id="originalPrice"
                  type="number"
                  step="0.01"
                  value={formData.originalPrice}
                  onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                  placeholder="1299.00"
                />
              </div>
            </div>

            {/* 图片上传区域 */}
            <div>
              <Label>商品图片</Label>
              <div className="mt-2 space-y-4">
                {/* 上传按钮 */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <Upload className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {uploadingImage ? '上传中...' : '点击上传图片'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          handleImageUpload(e.target.files);
                        }
                      }}
                      disabled={uploadingImage}
                    />
                  </label>
                  <span className="text-xs text-gray-500">支持 jpg、png、gif、webp 格式，单张图片最大 5MB</span>
                </div>

                {/* 已上传的图片列表 */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-4">
                    {uploadedImages.map((url) => (
                      <div key={url} className="relative group">
                        <img
                          src={url}
                          alt="商品图片"
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        {/* 封面图标记 */}
                        {coverImage === url && (
                          <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                            封面
                          </div>
                        )}
                        {/* 操作按钮 */}
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSetCover(url)}
                            className="text-xs"
                          >
                            {coverImage === url ? '已是封面' : '设为封面'}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRemoveImage(url)}
                            className="text-xs"
                          >
                            删除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="purchaseUrl">购买链接</Label>
              <Input
                id="purchaseUrl"
                value={formData.purchaseUrl}
                onChange={(e) => setFormData({ ...formData, purchaseUrl: e.target.value })}
                placeholder="https://item.taobao.com/item.htm?id=123456"
              />
            </div>

            <div>
              <Label htmlFor="purchasePlatform">购买平台</Label>
              <select
                id="purchasePlatform"
                value={formData.purchasePlatform}
                onChange={(e) => setFormData({ ...formData, purchasePlatform: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">请选择平台</option>
                <option value="taobao">淘宝</option>
                <option value="pinduoduo">拼多多</option>
                <option value="jd">京东</option>
                <option value="other">其他</option>
              </select>
            </div>

            <div>
              <Label htmlFor="description">商品描述（支持 Markdown）</Label>
              <MarkdownEditor
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value || '' })}
                height={300}
                placeholder="商品详细描述，支持 Markdown 格式

示例：
## 产品特点
- **轻量设计**：仅重 20g
- **长续航**：7天超长续航

### 技术规格
| 项目 | 参数 |
|------|------|
| 电池容量 | 180mAh |
| 充电时间 | 2小时 |

```代码块也可以高亮显示```"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              取消
            </Button>
            <Button onClick={handleSave} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  保存中...
                </>
              ) : (
                '保存'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </LayoutWithSidebar>
  );
}
